/**
 * Client-side contract for durable Quiz run persistence.
 *
 * Gameplay owns the snapshots. This module owns turning them into ordered,
 * revisioned writes and one completion request. No Supabase or storage types
 * leak through this interface; callers can provide adapters for either.
 */

export type QuizRunPersistenceIdentity = {
  quizId: string;
  editionId: string;
  userId: string;
  isAnonymous: boolean;
  /** In-memory fence supplied by the active run identity owner. */
  identityGeneration: number;
};

export type TriviaSnapshot = {
  editionItemId: string;
  guessSequence: readonly number[];
  rating?: number | null;
  feedback?: number | null;
  feedbackText?: string | null;
  xpEarned: number;
};

export type PollSnapshot = {
  editionItemId: string;
  pollDefinitionId: string;
  optionId: string;
  sessionId?: string | null;
  /** Optional client estimate; the server remains authoritative. */
  xpEarned?: number;
};

/**
 * Cumulative unresolved progress for one Quiz item.
 * An empty sequence records the view; trivia checkpoints then contain only
 * wrong canonical answer indexes (1-3). The resolved fact remains separate.
 */
export type QuizItemProgressSnapshot = {
  editionItemId: string;
  guessSequence: readonly number[];
};

export type ItemProgressWrite = {
  identity: QuizRunPersistenceIdentity;
  snapshot: QuizItemProgressSnapshot;
};

export type PersistedTriviaItem = TriviaSnapshot & {
  itemType: 'trivia';
  revision: number;
  acknowledgedRevision: number;
};

export type PersistedPollItem = PollSnapshot & {
  itemType: 'poll';
  revision: number;
  acknowledgedRevision: number;
};

export type PersistedItem = PersistedTriviaItem | PersistedPollItem;

export type CanonicalTriviaItem = {
  editionItemId: string;
  itemType: 'trivia';
  /** Hydrated unresolved journey checkpoint, kept separate from the answer fact. */
  progress?: QuizItemProgressSnapshot;
  /** Hydrated fact, when the server already has one. */
  fact?: Partial<TriviaSnapshot> & {
    revision?: number;
    acknowledgedRevision?: number;
  };
};

export type CanonicalPollItem = {
  editionItemId: string;
  itemType: 'poll';
  pollDefinitionId: string;
  /** Hydrated unresolved view checkpoint, kept separate from the poll response fact. */
  progress?: QuizItemProgressSnapshot;
  /** Hydrated fact, when the server already has one. */
  fact?: Partial<PollSnapshot> & {
    optionId?: string;
    revision?: number;
    acknowledgedRevision?: number;
  };
};

export type CanonicalItem = CanonicalTriviaItem | CanonicalPollItem;

export type TriviaWrite = {
  kind: 'trivia';
  identity: QuizRunPersistenceIdentity;
  snapshot: PersistedTriviaItem;
};

export type PollWrite = {
  kind: 'poll';
  identity: QuizRunPersistenceIdentity;
  snapshot: PersistedPollItem;
};

export type CompletionItem =
  (PersistedTriviaItem & { itemType: 'trivia' }) | (PersistedPollItem & { itemType: 'poll' });

export type CompletionRequest = {
  identity: QuizRunPersistenceIdentity;
  items: readonly CompletionItem[];
};

export type QuizCompletion = {
  completionId: string;
  quizId: string;
  editionId: string;
  emojiResults: string;
  accuracy: number;
  totalQuestions: number;
  xpEarned: number;
  longestStreak: number;
  createdAt?: string;
};

export type QuizRunPersistenceTransport = {
  writeItemProgress(input: ItemProgressWrite): Promise<unknown>;
  writeTrivia(input: TriviaWrite): Promise<unknown>;
  writePoll(input: PollWrite): Promise<unknown>;
  finalizeCompletion(input: CompletionRequest): Promise<QuizCompletion>;
};

export type QuizRunPersistenceStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export type QuizRunPersistenceScheduler = {
  setTimeout(callback: () => void, delayMs: number): unknown;
  clearTimeout(handle: unknown): void;
};

export type QuizRunPersistenceLifecycle = {
  addEventListener?(event: 'online' | 'focus', listener: () => void): void;
  removeEventListener?(event: 'online' | 'focus', listener: () => void): void;
};

export type QuizRunPersistenceSnapshot = {
  version: 1;
  storageKey: string;
  identity: QuizRunPersistenceIdentity;
  items: readonly PersistedItem[];
  completionRequested: boolean;
  completion: QuizCompletion | null;
  completionReconciled: boolean;
  disposed: boolean;
};

export type QuizRunPersistenceOptions = {
  identity: QuizRunPersistenceIdentity;
  transport?: QuizRunPersistenceTransport;
  storage?: QuizRunPersistenceStorage;
  scheduler?: QuizRunPersistenceScheduler;
  lifecycle?: QuizRunPersistenceLifecycle;
  /** Browser/auth owner can close the fence when the active generation changes. */
  isIdentityCurrent?: () => boolean;
  /** Optional async cache/analytics reconciliation after immutable completion. */
  reconcileCompletion?: (completion: QuizCompletion) => void | Promise<void>;
  /** Preview uses deterministic local completion and ignores external effects. */
  preview?: boolean;
};

export type QuizRunPersistence = {
  recordItemProgress(snapshot: QuizItemProgressSnapshot): Promise<unknown>;
  stageTrivia(snapshot: TriviaSnapshot): Promise<unknown>;
  stagePoll(snapshot: PollSnapshot): Promise<unknown>;
  requestCompletion(): Promise<QuizCompletion | null>;
  recover(canonicalItems: readonly CanonicalItem[]): Promise<QuizRunPersistenceSnapshot>;
  getSnapshot(): QuizRunPersistenceSnapshot;
  dispose(reason?: QuizRunPersistenceCancellationReason): void;
};

export type QuizRunPersistenceCancellationReason = 'disposed' | 'owner_replaced';

export const RETRY_DELAYS_MS = [250, 1_000, 4_000, 10_000, 30_000] as const;

export class QuizRunPersistenceError extends Error {
  readonly code: string;
  readonly retryable: boolean;
  readonly status?: number;

  constructor(
    message: string,
    options: { code?: string; retryable?: boolean; status?: number } = {}
  ) {
    super(message);
    this.name = 'QuizRunPersistenceError';
    this.code = options.code ?? 'quiz_run_persistence_error';
    this.retryable = options.retryable ?? false;
    this.status = options.status;
  }
}

/** Expected cancellation when the React/auth owner retires a persistence instance. */
export class QuizRunPersistenceCancelledError extends QuizRunPersistenceError {
  readonly reason: QuizRunPersistenceCancellationReason;

  constructor(reason: QuizRunPersistenceCancellationReason) {
    super(
      reason === 'owner_replaced' ? 'Persistence owner was replaced' : 'Persistence is disposed',
      { code: 'quiz_run_persistence_cancelled' }
    );
    this.name = 'QuizRunPersistenceCancelledError';
    this.reason = reason;
  }
}

/** Expected control flow when persistence work is retired by lifecycle or a newer revision. */
export function isExpectedQuizRunPersistenceRetirement(
  error: unknown
): error is QuizRunPersistenceError {
  return (
    error instanceof QuizRunPersistenceError &&
    (error.code === 'quiz_run_persistence_cancelled' ||
      error.code === 'quiz_run_revision_superseded')
  );
}
